import { useEffect, useState } from 'react';
import { createDocValueStore } from './docStore';
import { fetchProductJobs, productRunsOf, type Job3D } from './threeDApi';

// THIS NOTICE'S RECONSTRUCTIONS OF THE WHOLE PRODUCT, for the submodel on screen — see
// productRunsOf, which is the filter, and ProductModelStep, which is the panel that makes
// them.
//
// Asked here rather than in each place that wants it, because two places do and they must
// agree: the panel lists them, and the part selector's first segment wears a mark saying
// whether the product has a model at all. A fetch in each with no shared signal is a mark
// that goes on saying "none" for as long as the page stays open after one is made.
//
// AND AN EPOCH, because the phases do not unmount. Phase 2 stays alive while you work in
// Phase 5 — that is what lets a run keep running while you read another phase — so a fetch
// on mount happens once per document and would never see a mesh made afterwards. Generating
// or deleting bumps this, and both readers re-read.
const epochStore = createDocValueStore<number>(0);

export const bumpProductRuns = (docId: string) =>
  epochStore.set(docId, epochStore.get(docId) + 1);

// THE LAST ANSWER, per document and colour, kept for the page's life: a reader mounting again
// (a phase opened, a panel drawn) starts from what is known rather than from nothing.
const lastRuns = new Map<string, Job3D[]>();

// `runs` is NULL UNTIL THE SERVER HAS ANSWERED, and never an empty list standing in for one.
// It started as `[]` — the same value as "there are none" — so for the length of a round trip
// the panel said "No 3D model of the whole product yet" over a product that had one. `failed`:
// the last ask did not come back — said as a failure, not drawn as an empty list.
export function useProductRuns(docId: string, submodel: string): { runs: Job3D[] | null; failed: boolean } {
  const epoch = epochStore.useValue(docId);
  const key = `${docId}|${submodel}`;
  const [state, setState] = useState<{ key: string; runs: Job3D[] | null; failed: boolean }>(
    () => ({ key, runs: lastRuns.get(key) ?? null, failed: false }),
  );
  useEffect(() => {
    if (!docId) return;
    let alive = true;
    // ASKED BY THE PRODUCT, not by the model: they are the same panel's runs whichever
    // model wrote them, so filtering on one would show part of them.
    fetchProductJobs(docId)
      .then((jobs) => {
        const runs = productRunsOf(jobs, docId, submodel);
        lastRuns.set(key, runs);
        if (alive) setState({ key, runs, failed: false });
      })
      .catch(() => {
        // What was known OF THIS KEY stays; another colour's runs are not this one's.
        if (alive) setState((was) => ({ key, runs: was.key === key ? was.runs : lastRuns.get(key) ?? null, failed: true }));
      });
    return () => { alive = false; };
  }, [docId, submodel, epoch, key]);
  // Another colour's answer is not this one's: until this one's lands, what is known of it.
  return state.key === key ? state : { runs: lastRuns.get(key) ?? null, failed: false };
}
